import { ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators'

import { combineEpicsIsolated } from 'utils/epicRegistry'

import { addToast } from 'containers/Toasts/actions'

import { GET_MALWARE_DETECTIONS_ASYNC } from '../constants'

import {
  buildProductMappingsTemplateFromPartner,
  COMPANIES_PER_PAGE,
  COMPANIES_SEARCH_PAGE_SIZE,
  GET_AGREEMENTS_ASYNC,
  GET_ALL_IRONSCALES_COMPANIES_ASYNC,
  GET_BOARD_STATUSES_ASYNC,
  GET_COMPANIES_ASYNC,
  GET_INTEGRATION_ASYNC,
  GET_PRIORITIES_ASYNC,
  GET_PRODUCTS_ASYNC,
  GET_SERVICE_BOARDS_ASYNC,
  GET_UNMAPPED_IRONSCALES_COMPANIES_ASYNC,
  LOAD_MORE_COMPANIES_ASYNC,
  MODAL_CLOSE_DELAY_MS,
  PLAN_NAME_MAPPING,
  SAVE_INTEGRATION_ASYNC,
  SEARCH_COMPANIES_ASYNC,
  SET_CURRENT_STEP,
  TEST_CONNECTION_ASYNC,
  WIZARD_STEPS,
} from './constants'
import { selectConnectWiseMappedCompanies } from './selectors'

// Helper function to extract credentials from state$ (reduces code duplication)
const getCredentialsFromStore = (state$) => {
  const formState = state$.value.connectWise || {}
  const connection = formState.connection || {}

  if (
    connection.connection_url &&
    connection.company_id &&
    connection.public_key &&
    connection.private_key
  ) {
    return {
      connection_url: connection.connection_url,
      company_id: connection.company_id,
      public_key: connection.public_key,
      private_key: connection.private_key,
      client_id: connection.client_id,
    }
  }
  return null
}

// Test Connection Epic
const testConnectionEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(TEST_CONNECTION_ASYNC.INITIATE),
    mergeMap((action) =>
      api.connectWisePSA.testConnection(action.payload).pipe(
        mergeMap((response) => {
          // rxfetch post returns the full ajax response object
          // Extract the actual response data
          const data = response.response || response

          const actions = [TEST_CONNECTION_ASYNC.onSuccess(data)]

          // Toast 2: "Come back" toast - DISABLED
          // Show "come back" toast if user is not on Company Mapping step
          // This invites them to proceed to map companies
          // if (currentStep !== WIZARD_STEPS.COMPANY_MAPPING) {
          //   actions.push(
          //     addToast({
          //       ds: true,
          //       message: 'You can now go back and map your company',
          //       type: 'success',
          //       ctaText: 'Go to stage',
          //       ctaAction: {
          //         type: SET_CURRENT_STEP,
          //         payload: WIZARD_STEPS.COMPANY_MAPPING,
          //       },
          //       id: 'ConnectWise_Come_Back_Map_Company',
          //     })
          //   )
          // }

          return of(...actions)
        }),
        catchError((error) =>
          of(epicsErrorHandler(TEST_CONNECTION_ASYNC.onError, error, [400, 403, 422, 500]))
        ),
        takeUntil(action$.pipe(ofType(TEST_CONNECTION_ASYNC.CANCEL)))
      )
    )
  )

// Unwrap rxfetch body — { data, page, pageSize, hasMore } camelCase on wire.
// rxfetch.get() already unwraps to the body; rxfetch.post() emits the full ajax
// response object with the body nested under `.response`. Handle both shapes.
const unwrapSearchBody = (response) => {
  const body = response?.response ?? response ?? {}
  return {
    results: Array.isArray(body.data) ? body.data : [],
    hasMore: !!(body.hasMore ?? body.has_more),
  }
}

// Search epic — INITIATE for a NEW query cancels in-flight prior typing (switchMap).
// Picks up wizard credentials from form state so Step 2 can search before
// Step 1's config is persisted (same pattern as getCompaniesEpic).
const searchCompaniesEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(SEARCH_COMPANIES_ASYNC.INITIATE),
    switchMap((action) => {
      const { q = '', page = 1, pageSize = COMPANIES_SEARCH_PAGE_SIZE } = action.payload || {}
      const credentials = getCredentialsFromStore(state$)
      return api.connectWisePSA.searchCompanies({ q, page, pageSize, credentials }).pipe(
        map((response) =>
          SEARCH_COMPANIES_ASYNC.onSuccess({ q, page, pageSize, ...unwrapSearchBody(response) })
        ),
        catchError((error) =>
          of(epicsErrorHandler(SEARCH_COMPANIES_ASYNC.onError, error, [400, 403, 404, 500]))
        ),
        takeUntil(action$.pipe(ofType(SEARCH_COMPANIES_ASYNC.CANCEL)))
      )
    })
  )

// Load-more epic — mergeMap so concurrent page fetches all complete.
// Search above starts at page 1; this drives page 2+ on infinite scroll.
const loadMoreCompaniesEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(LOAD_MORE_COMPANIES_ASYNC.INITIATE),
    mergeMap((action) => {
      const { q = '', page = 2, pageSize = COMPANIES_SEARCH_PAGE_SIZE } = action.payload || {}
      const credentials = getCredentialsFromStore(state$)
      return api.connectWisePSA.searchCompanies({ q, page, pageSize, credentials }).pipe(
        map((response) =>
          LOAD_MORE_COMPANIES_ASYNC.onSuccess({ q, page, pageSize, ...unwrapSearchBody(response) })
        ),
        catchError((error) =>
          of(epicsErrorHandler(LOAD_MORE_COMPANIES_ASYNC.onError, error, [400, 403, 404, 500]))
        ),
        takeUntil(action$.pipe(ofType(LOAD_MORE_COMPANIES_ASYNC.CANCEL)))
      )
    })
  )

// Get Companies Epic – with credentials uses POST (wizard); without uses GET (saved config, e.g. editing)
const getCompaniesEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_COMPANIES_ASYNC.INITIATE),
    mergeMap(() => {
      const credentials = getCredentialsFromStore(state$)
      return api.connectWisePSA.getCompanies(credentials).pipe(
        map((response) => {
          // get() returns response directly, post() returns {response: {...}}
          // Backend returns { data: [...], page, total, ... } – use array for dropdown options
          let data = response.data || response.response?.data || response
          if (
            data &&
            typeof data === 'object' &&
            !Array.isArray(data) &&
            Array.isArray(data.data)
          ) {
            data = data.data
          }
          if (!Array.isArray(data)) {
            data = []
          }
          return GET_COMPANIES_ASYNC.onSuccess(data)
        }),
        catchError((error) =>
          of(epicsErrorHandler(GET_COMPANIES_ASYNC.onError, error, [400, 403, 404, 500]))
        ),
        takeUntil(action$.pipe(ofType(GET_COMPANIES_ASYNC.CANCEL)))
      )
    })
  )

// Get IRONSCALES Companies Epic
// Fetches paginated list of IRONSCALES companies (page and page_size always sent)
// Unwrap response.data so reducer receives { data: [...], page, total } (backend body shape)
const getAllIronscalesCompaniesEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_ALL_IRONSCALES_COMPANIES_ASYNC.INITIATE),
    mergeMap((action) => {
      const page = action.payload?.page ?? 1
      return api.connectWisePSA.getIronscalesCompanies(page, COMPANIES_PER_PAGE).pipe(
        map((response) => {
          // get() returns the parsed JSON body; keep full body so reducer gets total, page, data
          const payload =
            response && typeof response.total === 'number'
              ? response
              : (response?.data ?? response?.response?.data ?? response)
          return GET_ALL_IRONSCALES_COMPANIES_ASYNC.onSuccess(payload)
        }),
        catchError((error) =>
          of(
            epicsErrorHandler(
              GET_ALL_IRONSCALES_COMPANIES_ASYNC.onError,
              error,
              [400, 403, 404, 500]
            )
          )
        ),
        takeUntil(action$.pipe(ofType(GET_ALL_IRONSCALES_COMPANIES_ASYNC.CANCEL)))
      )
    })
  )

// Get Unmapped Ironscales Companies Epic
// Fetches paginated list of unmapped IRONSCALES companies (page and page_size always sent)
const getUnmappedIronscalesCompaniesEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_UNMAPPED_IRONSCALES_COMPANIES_ASYNC.INITIATE),
    mergeMap((action) => {
      const page = action.payload?.page ?? 1
      // Exclude companies that have a ConnectWise company mapping (not just agreement)
      // This matches the backend logic: a company is "mapped" if it has a CompanyMapping entry
      const mappedCompanyIds = selectConnectWiseMappedCompanies(state$.value).reduce(
        (acc, { connectwiseCompany, id }) => {
          // A company is mapped if it has a ConnectWise company selected (regardless of agreement)
          if (connectwiseCompany) acc.push(id)
          return acc
        },
        []
      )

      return api.connectWisePSA
        .getUnmappedIronscalesCompanies(page, COMPANIES_PER_PAGE, mappedCompanyIds)
        .pipe(
          map((response) => {
            // get() returns the parsed JSON body; keep full body so reducer gets total, page, data
            const payload =
              response && typeof response.total === 'number'
                ? response
                : (response?.data ?? response?.response?.data ?? response)
            return GET_UNMAPPED_IRONSCALES_COMPANIES_ASYNC.onSuccess(payload)
          }),
          catchError((error) =>
            of(
              epicsErrorHandler(
                GET_UNMAPPED_IRONSCALES_COMPANIES_ASYNC.onError,
                error,
                [400, 403, 404, 500]
              )
            )
          ),
          takeUntil(action$.pipe(ofType(GET_UNMAPPED_IRONSCALES_COMPANIES_ASYNC.CANCEL)))
        )
    })
  )

// Get Agreements Epic – with credentials uses POST; without uses GET (saved config)
const getAgreementsEpic = (action$, state$, { api }) =>
  action$.pipe(
    ofType(GET_AGREEMENTS_ASYNC.INITIATE),
    mergeMap((action) => {
      const credentials = getCredentialsFromStore(state$)
      return api.connectWisePSA.getAgreements(action.payload.companyId, credentials).pipe(
        map((response) => {
          // get() returns response directly, post() returns {response: {...}}
          // Backend may return {data: [...], total: N} or just an array
          let data = response.data || response.response?.data || response
          // If data is an object with a data property, extract it
          if (data && typeof data === 'object' && !Array.isArray(data) && data.data) {
            data = data.data
          }
          return GET_AGREEMENTS_ASYNC.onSuccess({
            data,
            companyId: action.payload.ironScalesCompanyId,
          })
        }),
        catchError((error) =>
          of(
            GET_AGREEMENTS_ASYNC.onError({
              companyId: action.payload.ironScalesCompanyId,
              error,
            })
          )
        ),
        takeUntil(action$.pipe(ofType(GET_AGREEMENTS_ASYNC.CANCEL)))
      )
    })
  )

// Get Products Epic – with credentials uses POST; without uses GET (saved config)
const getProductsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_PRODUCTS_ASYNC.INITIATE),
    mergeMap(() => {
      const credentials = getCredentialsFromStore(state$)
      return api.connectWisePSA.getProducts(credentials).pipe(
        map((response) => {
          // get() returns response directly, post() returns {response: {...}}
          // Backend returns { data: [...], total, ... } – use array for dropdown options
          let data = response.data || response.response?.data || response
          if (
            data &&
            typeof data === 'object' &&
            !Array.isArray(data) &&
            Array.isArray(data.data)
          ) {
            data = data.data
          }
          if (!Array.isArray(data)) {
            data = []
          }
          return GET_PRODUCTS_ASYNC.onSuccess(data)
        }),
        catchError((error) =>
          of(epicsErrorHandler(GET_PRODUCTS_ASYNC.onError, error, [400, 403, 404, 500]))
        ),
        takeUntil(action$.pipe(ofType(GET_PRODUCTS_ASYNC.CANCEL)))
      )
    })
  )

// Get Service Boards Epic – with credentials uses POST; without uses GET (saved config)
const getServiceBoardsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_SERVICE_BOARDS_ASYNC.INITIATE),
    mergeMap(() => {
      const credentials = getCredentialsFromStore(state$)
      return api.connectWisePSA.getServiceBoards(credentials).pipe(
        map((response) => {
          // get() returns response directly, post() returns {response: {...}}
          // Backend returns { data: [...], total, ... } – use array for dropdown options
          let data = response.data || response.response?.data || response
          if (
            data &&
            typeof data === 'object' &&
            !Array.isArray(data) &&
            Array.isArray(data.data)
          ) {
            data = data.data
          }
          if (!Array.isArray(data)) {
            data = []
          }
          return GET_SERVICE_BOARDS_ASYNC.onSuccess(data)
        }),
        catchError((error) =>
          of(epicsErrorHandler(GET_SERVICE_BOARDS_ASYNC.onError, error, [400, 403, 404, 500]))
        ),
        takeUntil(action$.pipe(ofType(GET_SERVICE_BOARDS_ASYNC.CANCEL)))
      )
    })
  )

// Get Board Statuses Epic – with credentials uses POST; without uses GET (saved config)
const getBoardStatusesEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_BOARD_STATUSES_ASYNC.INITIATE),
    mergeMap((action) => {
      const credentials = getCredentialsFromStore(state$)
      return api.connectWisePSA.getStatuses(action.payload.boardId, credentials).pipe(
        map((response) => {
          // get() returns response directly, post() returns {response: {...}}
          // Backend returns { data: [...], total, ... } – use array for dropdown options
          let data = response.data || response.response?.data || response
          if (
            data &&
            typeof data === 'object' &&
            !Array.isArray(data) &&
            Array.isArray(data.data)
          ) {
            data = data.data
          }
          if (!Array.isArray(data)) {
            data = []
          }
          return GET_BOARD_STATUSES_ASYNC.onSuccess(data)
        }),
        catchError((error) =>
          of(epicsErrorHandler(GET_BOARD_STATUSES_ASYNC.onError, error, [400, 403, 404, 500]))
        ),
        takeUntil(action$.pipe(ofType(GET_BOARD_STATUSES_ASYNC.CANCEL)))
      )
    })
  )

// Get Priorities Epic – with credentials uses POST; without uses GET (saved config)
const getPrioritiesEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_PRIORITIES_ASYNC.INITIATE),
    mergeMap(() => {
      const credentials = getCredentialsFromStore(state$)
      return api.connectWisePSA.getPriorities(credentials).pipe(
        map((response) => {
          // get() returns response directly, post() returns {response: {...}}
          // Backend returns { data: [...], total, ... } – use array for dropdown options
          let data = response.data || response.response?.data || response
          if (
            data &&
            typeof data === 'object' &&
            !Array.isArray(data) &&
            Array.isArray(data.data)
          ) {
            data = data.data
          }
          if (!Array.isArray(data)) {
            data = []
          }
          return GET_PRIORITIES_ASYNC.onSuccess(data)
        }),
        catchError((error) =>
          of(epicsErrorHandler(GET_PRIORITIES_ASYNC.onError, error, [400, 403, 404, 500]))
        ),
        takeUntil(action$.pipe(ofType(GET_PRIORITIES_ASYNC.CANCEL)))
      )
    })
  )

// Get Integration Epic - Load existing config for editing
const getIntegrationEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_INTEGRATION_ASYNC.INITIATE),
    mergeMap(() =>
      api.connectWisePSA.getIntegration().pipe(
        mergeMap((response) => {
          const appData = state$.value?.app?.data || {}
          const partnerPayload = appData?.user?.partner || appData?.partner || {}
          const productMappingsTemplate = buildProductMappingsTemplateFromPartner(
            partnerPayload,
            appData?.isPax8Hierarchy
          )
          const actions = [
            GET_INTEGRATION_ASYNC.onSuccess({
              response,
              productMappingsTemplate,
            }),
          ]

          // Toast 3: Configuration required toast - DISABLED
          // Show configuration required toast if integration exists but ticket board is missing
          // if (integrationData && integrationData.status !== 'inactive' && !hasTicketBoard) {
          //   actions.push(
          //   addToast({
          //     ds: true,
          //     message:
          //       'Attention: Configuration of your ConnectWise Service Board is required in order to receive incident tickets.',
          //     boldInMessage: 'Attention:',
          //     ctaText: 'Learn more',
          //     ctaInternalRedirect: '/settings/malware-detection?integration=connectwise',
          //     type: 'warning',
          //     id: 'ConnectWise_Configuration_Required',
          //   })
          //   )
          // }

          // Preload dropdown data when editing existing integration so dropdowns show full lists
          // (GET uses saved config; no credentials in state$ so getCredentialsFromStore returns null)
          const integrationData = response?.data ?? response
          if (
            integrationData &&
            (integrationData.connection_url ||
              integrationData.cw_base_url ||
              integrationData.cwBaseUrl)
          ) {
            const ticketBoard = integrationData.ticket_board || integrationData.ticketBoard
            const boardId =
              ticketBoard?.defaultBoardId ||
              ticketBoard?.boards?.[0]?.boardId ||
              ticketBoard?.boards?.[0]?.board_id
            const preloadActions = [
              // Lazy-browse first page instead of fetch-all (5K+ rows).
              SEARCH_COMPANIES_ASYNC.initiate({
                q: '',
                page: 1,
                pageSize: COMPANIES_SEARCH_PAGE_SIZE,
              }),
              GET_PRODUCTS_ASYNC.initiate(),
              GET_SERVICE_BOARDS_ASYNC.initiate(),
              GET_PRIORITIES_ASYNC.initiate(),
              ...(boardId ? [GET_BOARD_STATUSES_ASYNC.initiate({ boardId: String(boardId) })] : []),
            ]
            actions.push(...preloadActions)

            // Preload agreements for mapped companies that have a CW company selected
            const companyMappings =
              integrationData.company_mappings || integrationData.companyMappings || []
            companyMappings.forEach((cm) => {
              const cwCompanyId = cm.cw_company_id || cm.cwCompanyId
              const ironscalesCompanyId = cm.ironscales_company_id || cm.ironscalesCompanyId
              if (cwCompanyId) {
                actions.push(
                  GET_AGREEMENTS_ASYNC.initiate({
                    companyId: String(cwCompanyId),
                    ironScalesCompanyId: ironscalesCompanyId,
                  })
                )
              }
            })
          }

          return of(...actions)
        }),
        catchError((error) =>
          of(epicsErrorHandler(GET_INTEGRATION_ASYNC.onError, error, [400, 404, 500]))
        ),
        takeUntil(action$.pipe(ofType(GET_INTEGRATION_ASYNC.CANCEL)))
      )
    )
  )

// Transform frontend form data to backend format
const transformFormDataToBackendFormat = (formState, state$) => {
  const { connection, mappedCompanies, productMappings, ticketBoard } = formState
  const state = state$.value
  const priorities = state.connectWise?.priorities || []
  const priorityOptions = priorities.map((priority) => ({
    value: String(priority.id || priority.value),
    label: priority.name || priority.label || String(priority.id),
  }))

  // Transform product mappings
  const product_mappings = productMappings
    .filter((pm) => pm.connectwiseProduct) // Only include mapped products
    .map((pm) => {
      // Select component stores just the value (identifier or ID as string)
      // Backend returns {value: identifier, label: name} format
      // We need to extract ID from the products list if available
      const productValue = pm.connectwiseProduct
      const planName =
        pm.ironscalesPlan ||
        PLAN_NAME_MAPPING[pm.label] ||
        pm.label.toLowerCase().replaceAll(/\s+/g, '_')

      // Backend now returns ID as value, so we can parse it directly
      const productId = Number.parseInt(productValue, 10)

      return {
        ironscales_plan: planName,
        plan_category: pm.category,
        cw_product_id: productId,
        cw_product_identifier: productValue, // Will be resolved by backend from ID
        cw_product_name: pm.connectwiseProductName || productValue,
        unit_price: pm.unitPrice || null,
      }
    })

  // Transform company mappings
  const company_mappings = mappedCompanies
    .filter((cm) => {
      const hasCompany = !!cm.connectwiseCompany
      const hasAgreement = !!cm.connectwiseAgreement

      return hasCompany && hasAgreement
    })
    .map((cm) => {
      const companyValue = cm.connectwiseCompany
      const agreementValue = cm.connectwiseAgreement

      const companyId = Number.parseInt(companyValue, 10)
      const agreementId = agreementValue ? Number.parseInt(agreementValue, 10) : null

      const selectedAgreement = (cm.availableAgreements || []).find(
        (a) => String(a.value) === String(agreementValue)
      )
      const agreementName = selectedAgreement?.label || cm.connectwiseAgreementName || ''

      return {
        ironscales_company_id: cm.id,
        ironscales_company_name: cm.ironscalesCompany,
        cw_company_id: companyId,
        cw_company_identifier: cm.cwCompanyIdentifier || '',
        cw_company_name:
          cm.cwCompanyName || cm.connectwiseCompanyName || cm.ironscalesCompany || '',
        cw_agreement_id: agreementId,
        cw_agreement_name: agreementName,
        sync_enabled: cm.syncEnabled !== false,
        ignored: cm.ignored || false,
      }
    })

  // Transform ticket board config
  // Phase 1: Simple structure with single board, status, and priority
  // Backward compatibility: support old structure (boards array)
  let boards = ticketBoard.boards || []
  let defaultBoardId = ticketBoard.defaultBoardId || ticketBoard.serviceBoard || null

  // Normalize simple form values (Select may pass number or string)
  const serviceBoard =
    ticketBoard.serviceBoard != null && ticketBoard.serviceBoard !== ''
      ? String(ticketBoard.serviceBoard)
      : null
  const statusVal =
    ticketBoard.status != null && ticketBoard.status !== '' ? String(ticketBoard.status) : null
  const priorityVal =
    ticketBoard.priority != null && ticketBoard.priority !== ''
      ? String(ticketBoard.priority)
      : null
  const closedStatusVal =
    ticketBoard.closedStatus != null && ticketBoard.closedStatus !== ''
      ? Number.parseInt(String(ticketBoard.closedStatus), 10)
      : null
  const closedStatusName = ticketBoard.closedStatusName || ''

  // Prefer flat form fields (current user selection) so status/priority changes in edit mode are saved.
  // When boards was loaded from backend, user may have changed status/priority in the UI; we must use those.
  if (serviceBoard && statusVal && priorityVal) {
    boards = [
      {
        boardId: serviceBoard,
        boardName: ticketBoard.serviceBoardName || '',
        statuses: [
          {
            statusId: statusVal,
            statusName: ticketBoard.statusName || '',
            priorityId: priorityVal,
            priorityName: ticketBoard.priorityName || '',
          },
        ],
      },
    ]
    defaultBoardId = serviceBoard
  }

  const status_mappings = []
  const statusPriorityMap = {} // Map boardId_statusId -> priority_id for later use

  boards.forEach((board) => {
    if (board && Array.isArray(board?.statuses)) {
      const boardId = Number.parseInt(board.boardId, 10)
      if (Number.isNaN(boardId) || boardId <= 0) {
        // Skip invalid board - validation will catch this on backend
        return
      }

      board.statuses.forEach((status) => {
        // Validate that statusId exists and is a valid number
        // Also require that a priority is selected (priorityId must be set)
        if (status?.statusId && status?.priorityId) {
          const statusId = Number.parseInt(status.statusId, 10)
          const priorityId = Number.parseInt(status.priorityId, 10)
          const priorityName =
            priorityOptions.find((p) => String(p.value) === String(status.priorityId))?.label ||
            status.priorityName ||
            'Priority'

          // Only add if both statusId and priorityId are valid numbers
          if (
            !Number.isNaN(statusId) &&
            statusId > 0 &&
            !Number.isNaN(priorityId) &&
            priorityId > 0
          ) {
            status_mappings.push({
              cw_board_id: boardId,
              ironscales_status: 'open', // Default status, can be extended
              cw_status_id: statusId,
              cw_status_name: status.statusName || 'Status', // Will be updated by backend
              cw_priority_id: priorityId,
              cw_priority_name: priorityName,
            })
            // Store priority mapping for this board+status combination
            statusPriorityMap[`${boardId}_${status.statusId}`] = priorityId
          }
          // Skip invalid status - validation will catch this on backend
        }
        // Skip status without required fields - validation will catch this on backend
      })
    }
  })

  // Build severity mappings
  // Use the first priority from status mappings as default
  // This can be enhanced to support severity-specific priorities per board/status
  const defaultPriorityId =
    Object.keys(statusPriorityMap).length > 0
      ? statusPriorityMap[Object.keys(statusPriorityMap)[0]]
      : 0

  const severity_mappings = [
    {
      ironscales_severity: 'critical',
      cw_priority_id: defaultPriorityId,
      cw_priority_name: 'Critical', // Will be updated by backend
    },
    {
      ironscales_severity: 'high',
      cw_priority_id: defaultPriorityId,
      cw_priority_name: 'High', // Will be updated by backend
    },
    {
      ironscales_severity: 'medium',
      cw_priority_id: defaultPriorityId,
      cw_priority_name: 'Medium', // Will be updated by backend
    },
    {
      ironscales_severity: 'low',
      cw_priority_id: defaultPriorityId,
      cw_priority_name: 'Low', // Will be updated by backend
    },
  ]

  const boardId = defaultBoardId ? Number.parseInt(defaultBoardId, 10) : null

  // Get ConnectWise company ID from first company mapping if available
  const firstCompanyMapping = company_mappings.length > 0 ? company_mappings[0] : null
  const cw_psa_company_id = firstCompanyMapping?.cw_company_id || null

  // Build the payload - only include default_board_id if it's set (since ticket board step is skipped)
  const payload = {
    // Connection credentials
    connection_url: connection.connection_url,
    company_id: connection.company_id,
    public_key: connection.public_key,
    private_key: connection.private_key,
    client_id: connection.client_id || '22fae7c9-8587-4ce7-a52f-0c9bca94dd3f',

    // Product mappings
    product_mappings,

    // Company mappings
    company_mappings,

    // Ticket board config
    cw_psa_company_id, // Use company ID from first mapping
    default_agreement_id: firstCompanyMapping?.cw_agreement_id || null,
    severity_mappings,
    status_mappings,
  }

  // Only include default_board_id if it's set (since ticket board step is skipped)
  if (boardId !== null && boardId !== undefined) {
    payload.default_board_id = boardId
  }

  payload.default_closed_status_id = closedStatusVal || null
  payload.default_closed_status_name = closedStatusName

  return payload
}

// Save Integration Epic
const saveIntegrationEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(SAVE_INTEGRATION_ASYNC.INITIATE),
    mergeMap(() => {
      // Get full form state from Redux state$
      const formState = state$.value.connectWise || {}

      // Transform form data to backend format
      const backendPayload = transformFormDataToBackendFormat(formState, state$)

      // Validate that we have at least one company mapping
      if (!backendPayload.company_mappings || backendPayload.company_mappings.length === 0) {
        const errorMessage = 'Please map at least one company before saving the integration.'
        const error = {
          response: {
            status: 400,
            data: {
              success: false,
              errors: {
                company_mappings: [errorMessage],
              },
            },
          },
        }
        return of(
          epicsErrorHandler(SAVE_INTEGRATION_ASYNC.onError, error, [400, 422]),
          // Toast 4a: Validation error toast for company mappings - ENABLED
          addToast({
            message: errorMessage,
            type: 'error',
            id: 'ConnectWise_Save_Validation_Company_Mappings',
          })
        )
      }

      // Validate that cw_psa_company_id is set
      if (!backendPayload.cw_psa_company_id) {
        const errorMessage =
          'Please map at least one company to set the ConnectWise PSA company ID.'
        const error = {
          response: {
            status: 400,
            data: {
              success: false,
              errors: {
                cw_psa_company_id: [errorMessage],
              },
            },
          },
        }
        return of(
          epicsErrorHandler(SAVE_INTEGRATION_ASYNC.onError, error, [400, 422]),
          // Toast 4b: Validation error toast for PSA company ID - ENABLED
          addToast({
            message: errorMessage,
            type: 'error',
            id: 'ConnectWise_Save_Validation_PSA_Company_ID',
          }),
          {
            type: SET_CURRENT_STEP,
            payload: WIZARD_STEPS.COMPANY_MAPPING,
          }
        )
      }

      return api.connectWisePSA.enableIntegration(backendPayload).pipe(
        mergeMap((response) => {
          // Extract response data (rxfetch post returns {response: {...}})
          const data = response.response || response

          // Dispatch success actions first
          const successActions = of(
            SAVE_INTEGRATION_ASYNC.onSuccess(data),
            // Toast 5: Save success toast - ENABLED
            addToast({ message: data.message || 'ConnectWise integration saved successfully!' }),
            // Trigger refresh of malware detections to update the status indicator
            { type: GET_MALWARE_DETECTIONS_ASYNC.INITIATE }
          )

          // Close modal after a short delay to avoid React DOM unmounting race condition
          // This allows React to finish processing the success actions before closing
          setTimeout(() => {
            if (globalThis.closeModal && typeof globalThis.closeModal === 'function') {
              try {
                globalThis.closeModal()
              } catch (error) {
                // Silently fail if modal is already closed/unmounted
                // This can happen if the component unmounts before the timeout fires
                // Error is expected in this case, so we don't log it in production
                if (process.env.NODE_ENV === 'development') {
                  // eslint-disable-next-line no-console
                  console.debug('Failed to close modal (likely already unmounted):', error)
                }
              }
            }
          }, MODAL_CLOSE_DELAY_MS)

          return successActions
        }),
        catchError((error) =>
          of(epicsErrorHandler(SAVE_INTEGRATION_ASYNC.onError, error, [400, 422]))
        ),
        takeUntil(action$.pipe(ofType(SAVE_INTEGRATION_ASYNC.CANCEL)))
      )
    })
  )

export default combineEpicsIsolated(
  testConnectionEpic,
  getIntegrationEpic,
  getCompaniesEpic,
  searchCompaniesEpic,
  loadMoreCompaniesEpic,
  getAllIronscalesCompaniesEpic,
  getUnmappedIronscalesCompaniesEpic,
  getAgreementsEpic,
  getProductsEpic,
  getServiceBoardsEpic,
  getBoardStatusesEpic,
  getPrioritiesEpic,
  saveIntegrationEpic
)
